import { useState } from 'react';
import { Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { movies, movieCategories, faqItems } from '@/mocks/movies';

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.a
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "八戒影院 - 电影大全免费观看高清不缓冲",
  "description": "八戒影视为您打造电影大全免费观看库，高清不缓冲。涵盖最新热门影片与免费电影，电视剧全集同步更新，在线畅看。",
  "url": "https://bajiecinema.com/",
  "isPartOf": {
    "@type": "WebSite",
    "name": "八戒影院",
    "url": "https://bajiecinema.com/"
  }
};

const featuredMovies = movies.filter(m => m.featured);
const newMovies = movies.filter(m => m.isNew);
const tvSeries = movies.filter(m => m.category === 'tvseries');

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const filteredMovies = activeCategory === 'all' ? movies : movies.filter(m => m.category === activeCategory);

  return (
    <>
      <SchemaOrg data={faqSchema} />
      <SchemaOrg data={webPageSchema} />
      <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20cinema%20themed%20abstract%20art%20background%20with%20warm%20golden%20red%20coral%20gradients%20soft%20dramatic%20lighting%20cinematic%20atmosphere%20flowing%20silk%20ribbons%20artistic%20composition%20modern%20minimalist%20design%20dark%20edges%20for%20text%20contrast&width=1600&height=900&seq=hero-bajie-2026&orientation=landscape"
            alt="八戒影院 - 电影大全免费观看"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-background-50" />

        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-background-50 mb-4 leading-tight">
              畅享无限<span className="text-accent-400">影视世界</span>
            </h1>
            <p className="text-base md:text-lg text-background-200 mb-8 max-w-xl mx-auto">
              高清画质 · 海量资源 · 每日更新 · 免费观看
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                立即观影
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-all cursor-pointer whitespace-nowrap"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
              热门推荐
            </h2>
            <div className="flex items-center gap-1">
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-foreground-600"></i>
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-foreground-600"></i>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {featuredMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            最新上线
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {newMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900">
              影片分类
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              {movieCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {filteredMovies.slice(0, 12).map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          {filteredMovies.length > 12 && (
            <div className="text-center mt-8">
              <Link
                to={`/category/${activeCategory}`}
                className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                查看更多
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
            电视剧专区
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
            {tvSeries.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            关于八戒影院的常见疑问解答
          </p>
          <div className="flex flex-col gap-3">
            {faqItems.map((item, index) => (
              <details key={index} className="group bg-background-100 rounded-lg px-5 py-4 cursor-pointer">
                <summary className="text-sm font-medium text-foreground-800 flex items-center justify-between list-none">
                  {item.q}
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </div>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-primary-500">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-background-50 mb-3">
            订阅更新通知
          </h2>
          <p className="text-sm text-background-200 mb-6">
            第一时间获取最新影片上线信息和精彩推荐
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form="" action="https://readdy.ai/api/form/d9mvr6gbdkpk99j7geog" method="POST" onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
            if (honeypot) {
              alert('订阅成功！感谢您的关注。');
              form.reset();
              return;
            }
            try {
              const formData = new FormData(form);
              formData.delete('website_alt');
              const urlEncoded = new URLSearchParams(formData as any).toString();
              const resp = await fetch(form.action, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: urlEncoded,
              });
              const text = await resp.text();
              let parsed: any = {};
              try { parsed = JSON.parse(text); } catch (_e) { parsed = {}; }
              if (resp.ok && parsed?.code === 'OK') {
                alert('订阅成功！感谢您的关注。');
                form.reset();
              } else {
                const msg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试。';
                alert(msg);
              }
            } catch (_) {
              alert('网络错误，请稍后再试。');
            }
          }}>
            <input
              type="email"
              name="email"
              placeholder="请输入您的邮箱地址"
              className="flex-1 px-4 py-3 rounded-full bg-background-50 text-foreground-800 text-sm placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
              required
            />
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-accent-500 text-background-50 rounded-full font-medium text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              立即订阅
            </button>
          </form>
        </div>
      </section>
    </>
  );
}